import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { orderService } from '../../services/order.service';
import { theme } from '../../config/theme';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { PackageIcon } from '../../components/icons/Icons';

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'processing', label: 'Processing' },
  { key: 'packed', label: 'Packed' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'out_for_delivery', label: 'Out for Delivery' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'refunded', label: 'Refunded' },
];

const StatusBadge = ({ status }) => {
  const color = getStatusColor(status);
  const label = status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  return (
    <span style={{
      display: 'inline-block', padding: '4px 12px', borderRadius: theme.borderRadius.full,
      fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold,
      color: '#fff', backgroundColor: color,
    }}>{label}</span>
  );
};

const AdminOrders = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });

  const fetchOrders = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (activeTab) params.status = activeTab;
      const response = await orderService.getAllOrders(params);
      const data = response.data?.data || response.data;
      setOrders(data.orders || []);
      setPagination({ page: data.pagination?.page || 1, totalPages: data.pagination?.totalPages || 1 });
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { fetchOrders(1); }, [fetchOrders]);

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? theme.spacing.md : theme.spacing.xl, maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{
          fontSize: isMobile ? theme.typography.fontSize['2xl'] : theme.typography.fontSize['3xl'],
          fontWeight: theme.typography.fontWeight.bold,
          marginBottom: theme.spacing.xl,
        }}>
          Order Management
        </h1>

        {/* Status Filter Tabs */}
        <div style={{
          display: 'flex', gap: theme.spacing.sm, marginBottom: theme.spacing.xl,
          overflowX: 'auto', paddingBottom: theme.spacing.sm,
        }}>
          {STATUS_TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
              borderRadius: theme.borderRadius.full,
              border: `1px solid ${activeTab === tab.key ? theme.colors.primary.main : theme.colors.gray[300]}`,
              backgroundColor: activeTab === tab.key ? theme.colors.primary.main : 'transparent',
              color: activeTab === tab.key ? '#fff' : theme.colors.text.secondary,
              fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium,
              cursor: 'pointer', whiteSpace: 'nowrap', transition: theme.transitions.fast,
            }}>{tab.label}</button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : orders.length === 0 ? (
          <Card padding="xl">
            <div style={{ textAlign: 'center', padding: theme.spacing['2xl'] }}>
              <PackageIcon size={64} color={theme.colors.gray[300]} />
              <h3 style={{ marginTop: theme.spacing.lg, color: theme.colors.text.secondary }}>No orders found</h3>
            </div>
          </Card>
        ) : (
          <>
            {/* Table-like layout */}
            <Card padding="0">
              {/* Header */}
              {!isMobile && (
                <div style={{
                  display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 1fr 1fr 1fr 100px',
                  padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                  backgroundColor: theme.colors.gray[50],
                  borderBottom: `1px solid ${theme.colors.gray[200]}`,
                  fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold,
                  color: theme.colors.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  <span>Order</span>
                  <span>Customer</span>
                  <span>Total</span>
                  <span>Status</span>
                  <span>Date</span>
                  <span></span>
                </div>
              )}

              {orders.map((order, idx) => (
                <div key={order.id} style={{
                  display: isMobile ? 'flex' : 'grid',
                  flexDirection: isMobile ? 'column' : undefined,
                  gridTemplateColumns: isMobile ? undefined : '1.5fr 1.5fr 1fr 1fr 1fr 100px',
                  padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                  borderBottom: idx < orders.length - 1 ? `1px solid ${theme.colors.gray[100]}` : 'none',
                  alignItems: 'center',
                  gap: isMobile ? theme.spacing.sm : 0,
                  cursor: 'pointer',
                  transition: theme.transitions.fast,
                }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = theme.colors.gray[50]}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  onClick={() => navigate(`/admin/orders/${order.id}`)}
                >
                  <div>
                    <div style={{ fontWeight: theme.typography.fontWeight.semibold, fontSize: theme.typography.fontSize.sm }}>
                      #{order.order_number}
                    </div>
                    <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.disabled }}>
                      {order.items?.length || 0} item(s)
                    </div>
                  </div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                    {order.user ? `${order.user.first_name} ${order.user.last_name}` : 'Unknown'}
                    <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.disabled }}>
                      {order.user?.email || ''}
                    </div>
                  </div>
                  <div style={{ fontWeight: theme.typography.fontWeight.semibold, fontSize: theme.typography.fontSize.sm }}>
                    {formatCurrency(order.total)}
                  </div>
                  <div><StatusBadge status={order.status} /></div>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
                    {formatDate(order.createdAt || order.created_at)}
                  </div>
                  <div>
                    <Button variant="outline" size="sm" onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/orders/${order.id}`);
                    }}>View</Button>
                  </div>
                </div>
              ))}
            </Card>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: theme.spacing.sm, marginTop: theme.spacing.lg }}>
                <Button variant="outline" size="sm" disabled={pagination.page <= 1}
                  onClick={() => fetchOrders(pagination.page - 1)}>Previous</Button>
                <span style={{ alignSelf: 'center', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <Button variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchOrders(pagination.page + 1)}>Next</Button>
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default AdminOrders;
